import { DateInput, EventInput, SlotLabelContentArg } from '@fullcalendar/core';
import {
  differenceInMilliseconds,
  format,
  getHours,
  getMinutes,
  parse,
} from 'date-fns';

import { isBoolean, isEmpty, isNil, isNumber, isObject, pickBy } from 'lodash';
import moment from 'moment';
import { tTemplate, tTemplateV2 } from '../../Agenda.types';
import {
  tCreateEventForm,
  tEventResponse,
  tEventResponseV2,
  tGenericObject,
  tGetDuration,
  tUpdateEventRequest,
} from './Calendar.types';

// What the agenda should show in the "Lead asignado" column. `kind: 'key'` is an
// i18n key the caller translates; `kind: 'name'` is the lead's own name, which is
// never translated.
export type tLeadLabel =
  | { kind: 'name'; value: string }
  | { kind: 'key'; value: string };

// An event with no lead and an event whose lead failed to load arrive identical:
// calendar-service nils the whole lead object in both cases. `related_entity_id`
// is what separates them — it is present whenever the event is linked to a lead,
// resolved or not (BUG-1102).
export function resolveLeadLabel(args: {
  relatedEntityId?: string;
  leadId?: string;
  leadFullName?: string;
}): tLeadLabel {
  const { relatedEntityId, leadId, leadFullName } = args;

  if (leadFullName) return { kind: 'name', value: leadFullName };

  // `leadId` is what says the lead resolved — not its name. A lead with an empty
  // name still has a live chat link, so calling it "couldn't load" would
  // contradict the row the user can click. Fall back to the no-name label.
  if (leadId) return { kind: 'key', value: 'agenda/agenda:lead-no-name' };

  if (relatedEntityId) {
    return { kind: 'key', value: 'agenda/agenda:lead-unavailable' };
  }

  return { kind: 'key', value: 'agenda/agenda:no-lead' };
}

// The edit form (`tUpdateEventForm`) carries none of the lead fields, so rebuilding
// an event's `data` from it alone drops the lead and its chat link until the agenda
// is reloaded. The lead is not something the edit can change — it is carried over
// from the row being replaced (BUG-1102).
export function buildUpdatedEventData(args: {
  values: Record<string, unknown>;
  guests: string[];
  previousData?: { lead?: unknown; related_entity_id?: string };
}): Record<string, unknown> {
  const { values, guests, previousData } = args;

  return {
    ...values,
    guests,
    lead: previousData?.lead,
    related_entity_id: previousData?.related_entity_id,
  };
}

export function eventLegacyParser(
  event: tEventResponseV2,
  workspaceId: string,
): tEventResponse {
  return {
    id: event.id,
    event_type: event.eventType,
    template_id: event.templateId,
    workspace_id: workspaceId,
    name: event.name,
    start_date: event.startDate,
    end_date: event.endDate,
    user_id: event.creatorAuthID,
    note: event.note,
    address: event.address,
    links: event.links,
    description: event.description,
    guests: event.guests,
    reminder_time_minutes: event.reminderTimeMinutes,
    related_entity_id: event.relatedEntityId,
    lead: {
      full_name: event.lead?.name,
      id: event.lead?.id,
      profile_picture: event.lead?.profilePicture,
    },
  };
}

export function templateLegacyParser(
  template: tTemplateV2,
  workspaceId: string,
): tTemplate {
  return {
    id: template.id,
    workspace_id: workspaceId,
    event_type: template.eventType,
    name: template.name,
    emoji: template.emoji,
    reminder: template.reminder,
    reminder_time_minutes: template.reminderTimeMinutes,
    user_id: template.creatorAuthID,
    fields: template.fields,
  };
}

export const slotLabelContent = (arg: SlotLabelContentArg): string => {
  const date = new Date(arg.date);

  return format(date, 'hh:mm a');
};

export function parseHour(hourString: string): [number, number, number] {
  const date = parse(hourString, 'HH:mm:ss', new Date());

  return [date.getHours(), date.getMinutes(), date.getSeconds()];
}

export function parseTime(time: string): [number, number] {
  const date = parse(time, 'HH:mm:ss', new Date());
  const hours = getHours(date);
  const minutes = getMinutes(date);

  return [hours, minutes];
}

export const dateFormat = "yyyy-MM-dd'T'HH:mm:ssXXX";

export function getDuration(
  startDate: DateInput,
  endDate: DateInput,
): tGetDuration {
  if (!startDate || !endDate) {
    return {
      date: '',
      duration: '',
      time: '',
    };
  }

  const start = parse(startDate.toString(), dateFormat, new Date());
  const end = parse(endDate.toString(), dateFormat, new Date());
  const durationInMilliseconds = differenceInMilliseconds(end, start);
  const hours = Math.floor(durationInMilliseconds / 3600000);
  const minutes = Math.floor((durationInMilliseconds % 3600000) / 60000);
  const duration = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  const time = format(start, 'HH:mm');

  return {
    date: startDate.toString(),
    duration,
    time,
  };
}

export function getInterval(fromDate: tGetDuration): {
  startDate: string;
  endDate: string;
} {
  const date = new Date(fromDate.date);
  const time = new Date(fromDate.time);
  const minutes = time.getMinutes();
  const hours = time.getHours();

  const startDate = moment(date).set({ hours, minutes, seconds: 0 }).toDate();
  const endDate = moment(startDate).add(fromDate.duration, 'minutes').toDate();

  return {
    startDate: format(startDate, dateFormat),
    endDate: format(endDate, dateFormat),
  };
}

export function removeEmpty(obj: tGenericObject) {
  return pickBy(obj, (v) => {
    if (isObject(v)) {
      v = removeEmpty(v as tGenericObject);
      return !isEmpty(v);
    }
    return !isNil(v) && (v !== '' || isNumber(v) || isBoolean(v));
  });
}

export function getAllowedFields(
  templates: tTemplate[],
  values: tCreateEventForm,
): tGenericObject {
  const fields: tGenericObject = {
    description: false,
    duration: false,
    notes: false,
    links: false,
    address: false,
    users: false,
  };

  if (templates.length && values.templateId) {
    const selectedTemplate = templates?.find(
      (template) => template.id === values.templateId,
    );
    if (
      selectedTemplate &&
      selectedTemplate.fields &&
      selectedTemplate.fields.length
    ) {
      for (const field of selectedTemplate.fields) {
        const key = field.name as keyof tGenericObject;
        const value = field.required;

        fields[key] = value;
      }
    }
  }

  return fields;
}

export function formatTodayDate(date: Date): string {
  return moment(date).format('D [de] MMMM [de] YYYY');
}

export function removeFields(
  updateEventRequest: Partial<tUpdateEventRequest>,
  fields: tGenericObject,
) {
  const result = pickBy(updateEventRequest, (_, key) => fields[key] !== false);

  if (!fields.users) {
    delete result.guests;
  }

  return result;
}

// The subset of `event.data` mapEventsToResourceView reads. `guests` is `unknown`
// because the helper validates the runtime shape itself (Array.isArray + per-element
// type guard) — `EventInput.data` is `any` from FullCalendar's typings and the
// upstream `tEventResponse.guests: string[]` declaration is also a lie at runtime
// (the API returns `null` for ~54% of historical rows). `user_id` is required so a
// rename at the construction site surfaces as a compile error rather than a silent
// fallback regression.
export type tResourceMappableEventData = {
  guests?: unknown;
  user_id: string;
};

export type tResourceMappableEvent = EventInput & {
  data?: tResourceMappableEventData;
};

// Maps prepared FullCalendar events to the resourceTimeGridDay (Equipo) shape.
// One event becomes one row per guest, carrying that guest's authId as resourceId
// so FullCalendar can place it in the correct team-member column. When an event
// has no guests, the creator's authId is used as a fallback — without this,
// events stored with guests=null (the majority of historical rows) silently
// disappear from the Equipo view because FullCalendar drops events whose
// resourceId doesn't match any known column.
export function mapEventsToResourceView(
  events: tResourceMappableEvent[],
): EventInput[] {
  const nextEvents: EventInput[] = [];

  for (const event of events) {
    const rawGuests = event.data?.guests;
    const guests = Array.isArray(rawGuests)
      ? rawGuests.filter((g): g is string => typeof g === 'string')
      : [];

    if (guests.length) {
      for (const guest of guests) {
        nextEvents.push({ ...event, resourceId: guest });
      }
      continue;
    }

    const creatorAuthId = event.data?.user_id;
    if (typeof creatorAuthId === 'string' && creatorAuthId.length > 0) {
      nextEvents.push({ ...event, resourceId: creatorAuthId });
    } else {
      nextEvents.push(event);
    }
  }

  return nextEvents;
}
